import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { AgGridOptions, AgGridRowModelType, IModeView } from '@/shared/models/ag-grid.model'
import { Nullable } from '@/shared/types/app.types'
import { GridUtils } from '@/shared/util/grid-utils'
import {
    GetContextMenuItemsParams,
    SelectionChangedEvent,
    SortChangedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    MenuItemDef,
    RowSelectedEvent,
    RowClickedEvent
} from '@ag-grid-enterprise/all-modules'
import { Component, Mixins } from 'vue-property-decorator'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { initialCrudModelBaseState, CrudModelBase } from '../models/cruds.models'
import { GridColumnRequest } from '@/api/services/common.dto'

@Component
export default class BaseCrudServerSideComponent<T> extends Mixins<GridExportMixin>(GridExportMixin) {
    state: CrudModelBase<T>

    constructor() {
        super()
        this.state = initialCrudModelBaseState<T>()
    }

    created(): void {
        this.state.gridOptions = {
            paginationPageSize: 20,
            paginationAutoPageSize: false,
            sideBar: gridSideBarConfig,
            getContextMenuItems: this.getContextMenuItems,
            onGridReady: this.onGridReady,
            onSelectionChanged: this.onSelectionChanged,
            onSortChanged: this.onSortChanged,
            onFilterChanged: this.onFilterChanged,
            rowHeight: this.rowHeight,
            onRowSelected: this.onRowSelected,
            onRowClicked: this.onRowClicked,
            rowModelType: AgGridRowModelType.SERVER_SIDE
        }
    }

    get gridOptions(): AgGridOptions {
        return this.state.gridOptions
    }

    get hasActive(): boolean {
        return this.state.selectedRows.some((item: any) => item.active)
    }
    get hasDeactive(): boolean {
        return this.state.selectedRows.some((item: any) => !item.active)
    }

    get hasAllActivated(): boolean {
        return this.state.selectedRows.every((item: any) => item.active)
    }
    get hasAllDeactivated(): boolean {
        return this.state.selectedRows.every((item: any) => !item.active)
    }

    get canDeactivateBatch(): boolean {
        return !!this.state.selectedRows.length && this.hasActive && !this.hasAllDeactivated
    }
    get canActivateBatch(): boolean {
        return this.hasDeactive && !this.hasAllActivated
    }

    get rowHeight(): number {
        return this.state.viewOptions.modeView.size
    }

    onGridReady(event: GridReadyEvent): void {
        this.state.gridApi = event.api
        this.state.columnApi = event.columnApi

        if (this.state.columnsDef) {
            this.state.gridApi.setColumnDefs(this.state.columnsDef)
        }
        if (this.state.filterModel) {
            this.state.gridApi.setFilterModel(this.state.filterModel)
        }
        if (this.state.datasource) {
            this.state.gridApi.setServerSideDatasource(this.state.datasource)
        }
    }

    reloadData(deselectAll = true): void {
        if (this.state.gridApi && this.state.columnApi) {
            if (deselectAll) {
                this.state.gridApi.deselectAll()

                // force synchronous event calling immediately
                const event: SelectionChangedEvent = {
                    api: this.state.gridApi,
                    columnApi: this.state.columnApi,
                    type: 'selectionChanged'
                }
                this.onSelectionChanged(event)
            }
            this.state.gridApi.refreshServerSideStore({ purge: true })
        }
    }

    onSelectionChanged({ api }: SelectionChangedEvent): void {
        this.state = {
            ...this.state,
            selectedRows: api.getSelectedRows()
        }
    }

    onSortChanged({ api }: SortChangedEvent): void {
        api.deselectAll()
    }

    onFilterChanged({ api }: FilterChangedEvent): void {
        this.state.filterModel = api.getFilterModel()
        api.deselectAll()
    }

    showModal(title: string, selected: any, modalReference: any): void {
        this.state.actionTitle = title
        modalReference.show([selected])
    }

    changedPageSize(pageSize: number): void {
        this.state.gridApi?.paginationSetPageSize(pageSize)
    }

    changedMode(modeView: IModeView): void {
        this.state = {
            ...this.state,
            viewOptions: {
                ...this.state.viewOptions,
                modeView: {
                    ...this.state.viewOptions.modeView,
                    ...modeView
                }
            }
        }
        if (this.state.gridApi) {
            GridUtils.changeRowHeightGrid(this.state.gridApi, modeView.size)
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {
        return []
    }

    getColumnsToExport(): Nullable<GridColumnRequest[]> {
        return this.$_getColumnsToExport(this.state.gridApi, this.state.columnApi)
    }

    onRowSelected({ api }: RowSelectedEvent): void {
        this.state.selectedRows = api.getSelectedRows()
    }

    onRowClicked({ data }: RowClickedEvent): void {
        this.state.clickedRow = data
        this.state.visibleSidePanel = true
    }
}
